import {
  QuestGroupDraft,
  TagDto,
  TargetDraft,
} from '@/common/models/gamification'
import {
  attachQuestGroupTag,
  attachQuestTag,
  detachQuestGroupTag,
  detachQuestTag,
  getQuestGroupTags,
  getQuestTags,
  reorderQuestGroupTags,
  reorderQuestTags,
} from '@/core/services/gamification'

/** Minimal shape of an association-endpoint quadruple (quest or quest-group tags). */
interface TagAssociationApi {
  getTags: (id: string) => Promise<{ data: TagDto[] }>
  attachTag: (id: string, tagId: string) => Promise<{ data: TagDto[] }>
  detachTag: (id: string, tagId: string) => Promise<{ data: TagDto[] }>
  reorderTags: (id: string, tagIds: string[]) => Promise<{ data: TagDto[] }>
}

const questTagApi: TagAssociationApi = {
  getTags: getQuestTags,
  attachTag: attachQuestTag,
  detachTag: detachQuestTag,
  reorderTags: reorderQuestTags,
}

const questGroupTagApi: TagAssociationApi = {
  getTags: getQuestGroupTags,
  attachTag: attachQuestGroupTag,
  detachTag: detachQuestGroupTag,
  reorderTags: reorderQuestGroupTags,
}

/**
 * Reconciles a quest's or quest group's tag associations against a desired ordered list. The
 * server's current associations are fetched fresh here (rather than trusting the draft's
 * load-time snapshot) since the entity may have just been created/updated. Attaches missing tags,
 * detaches removed ones, then reorders if the resulting order still doesn't match what's desired.
 */
const reconcileTags = async (
  api: TagAssociationApi,
  entityId: string,
  desired: TagDto[],
): Promise<void> => {
  const { data: current } = await api.getTags(entityId)
  const currentIds = current.map((t) => t.id)
  const desiredIds = desired.map((t) => t.id)

  let latestIds = currentIds
  for (const tagId of currentIds.filter((id) => !desiredIds.includes(id))) {
    const { data } = await api.detachTag(entityId, tagId)
    latestIds = data.map((t) => t.id)
  }
  for (const tagId of desiredIds.filter((id) => !currentIds.includes(id))) {
    const { data } = await api.attachTag(entityId, tagId)
    latestIds = data.map((t) => t.id)
  }

  const orderMatches =
    latestIds.length === desiredIds.length &&
    latestIds.every((id, i) => id === desiredIds[i])
  if (!orderMatches && desiredIds.length > 0) {
    await api.reorderTags(entityId, desiredIds)
  }
}

export const reconcileQuestTags = (questId: string, desired: TagDto[]) =>
  reconcileTags(questTagApi, questId, desired)

export const reconcileQuestGroupTags = (groupId: string, desired: TagDto[]) =>
  reconcileTags(questGroupTagApi, groupId, desired)

/**
 * Merges the tags from a `hydrateTargetDraftTags` result into whatever the draft currently is,
 * matched by entity id — so edits the admin made while the (async) hydration fetch was in flight
 * are preserved instead of being clobbered by the stale pre-fetch snapshot. If the draft's kind or
 * entity id no longer matches (the user switched target), the fetched tags are simply dropped.
 */
export const mergeHydratedTags = (
  prev: TargetDraft,
  hydrated: TargetDraft,
): TargetDraft => {
  if (prev.kind === 'QUEST' && hydrated.kind === 'QUEST') {
    if (!prev.quest || !hydrated.quest || prev.quest.id !== hydrated.quest.id) {
      return prev
    }
    return {
      kind: 'QUEST',
      quest: { ...prev.quest, tags: hydrated.quest.tags },
    }
  }
  if (prev.kind === 'QUEST_GROUP' && hydrated.kind === 'QUEST_GROUP') {
    if (prev.group.id !== hydrated.group.id) return prev
    const tagsByQuestId = new Map(
      hydrated.group.quests
        .filter((q) => q.id)
        .map((q) => [q.id as string, q.tags]),
    )
    return {
      kind: 'QUEST_GROUP',
      group: {
        ...prev.group,
        tags: hydrated.group.tags,
        quests: prev.group.quests.map((q) =>
          q.id && tagsByQuestId.has(q.id)
            ? { ...q, tags: tagsByQuestId.get(q.id) }
            : q,
        ),
      },
    }
  }
  return prev
}

/**
 * Hydrates a freshly-built target draft (from `stepToTargetDraft`) with each quest's/group's
 * currently-attached tags — the main quest/quest-group wire responses don't carry `tags`
 * (it's a separate association endpoint), so this is a best-effort fetch purely for display when
 * opening the step editor in edit mode. Never-persisted (no `id`) quests/groups have no
 * associations yet, so they're left with an empty list.
 */
export const hydrateTargetDraftTags = async (
  draft: TargetDraft,
): Promise<TargetDraft> => {
  switch (draft.kind) {
    case 'QUEST': {
      if (!draft.quest?.id) return draft
      const { data } = await getQuestTags(draft.quest.id)
      return { kind: 'QUEST', quest: { ...draft.quest, tags: data ?? [] } }
    }
    case 'QUEST_GROUP': {
      const group: QuestGroupDraft = draft.group
      const [groupTags, ...questTags] = await Promise.all([
        group.id
          ? getQuestGroupTags(group.id).then((r) => r.data ?? [])
          : Promise.resolve<TagDto[]>([]),
        ...group.quests.map((q) =>
          q.id
            ? getQuestTags(q.id).then((r) => r.data ?? [])
            : Promise.resolve<TagDto[]>([]),
        ),
      ])
      return {
        kind: 'QUEST_GROUP',
        group: {
          ...group,
          tags: groupTags,
          quests: group.quests.map((q, i) => ({ ...q, tags: questTags[i] })),
        },
      }
    }
    default:
      return draft
  }
}
